import React, { useCallback, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import Modal from "../../Modal";
import ScreenCard from "../../../screens/ScreenCard";
import SimpleHeader from "../../SimpleHeader";
import { Controller, useForm } from "react-hook-form";
import TextField from "../../TextField";
import { useI18n } from "../../../hooks/i18n-hooks";
import { useTheme } from "../../../hooks/use-theme";
import Text from "../../Text";
import { formatCurrency, toFixedIfNecessary } from "../../../utils";
import MarkdownView from "../../MarkdownView";
import VStack from "../../../ui_atoms/VStack";
import { useValidateNumber } from "./PayrollBreakdownEditor";
import { BodyMediumText, BodySmallText } from "../../FormattedText";

type FormData = {
    nbHours: string;
};

type OvertimePaidCreateModalProps = {
    hourlyInvoiceAmount: number;
    formattedEndOfMonthOvertimeTotal: number;
    onCreate: (overtimePaid100: number) => void;
    onClose: () => void;
    visible: boolean;
};

const OvertimePaidCreateModal: React.FC<OvertimePaidCreateModalProps> = ({
    hourlyInvoiceAmount,
    formattedEndOfMonthOvertimeTotal,
    onClose,
    onCreate,
    visible,
}) => {
    const styles = useStyles();
    const { colors } = useTheme();
    const i18n = useI18n();
    const validateNumber = useValidateNumber();

    const {
        control,
        handleSubmit,
        formState: { isValid, errors },
        watch,
    } = useForm<FormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            nbHours: "",
        },
    });

    const nbHours = watch("nbHours");

    const currentNbHours = useMemo(
        () => Number(formattedEndOfMonthOvertimeTotal) - (Number(nbHours) || 0),
        [nbHours, formattedEndOfMonthOvertimeTotal],
    );

    const handleCreateOvertimePaid100 = useCallback(
        (formData: FormData) => {
            onCreate(Number(formData.nbHours));
        },
        [onCreate],
    );

    const validateOvertimeWorkDelta = useCallback(
        (value?: string) => {
            if (!value) {
                return undefined;
            }
            return validateNumber(value) || Number(value) > formattedEndOfMonthOvertimeTotal
                ? i18n.t("overtimePaid.overtime_paid_too_high", {
                      deltaWorkHours: formattedEndOfMonthOvertimeTotal,
                  })
                : undefined;
        },
        [formattedEndOfMonthOvertimeTotal, i18n, validateNumber],
    );

    return (
        <Modal visible={visible} onClose={onClose} hideCloseButton animationType="slide">
            <SimpleHeader title={i18n.t("overtimePaid.title")} onClose={onClose} mode="small" />
            <ScreenCard
                buttons={[
                    {
                        onPress: handleSubmit(handleCreateOvertimePaid100),
                        mode: "secondary",
                        isValid: isValid,
                        title: i18n.t("add"),
                    },
                ]}
            >
                <VStack gap="2/3" h="2/3">
                    <BodyMediumText>{i18n.t("overtimePaid.amount_title")}</BodyMediumText>
                    <View
                        style={[
                            styles.container,
                            currentNbHours === 0 && {
                                height: 0,
                                paddingBottom: 0,
                            },
                        ]}
                    >
                        <MarkdownView
                            fontColor={colors.secondary}
                            markdown={i18n.t("overtimePaid.delta_work_hours_info", {
                                symbol: currentNbHours > 0 ? "+" : "-",
                                deltaWorkHours: toFixedIfNecessary(Math.abs(currentNbHours), 2),
                                overtimeAmount: formatCurrency({
                                    amountInCents: hourlyInvoiceAmount * Math.abs(currentNbHours),
                                }),
                            })}
                        />
                    </View>
                    <Controller
                        name="nbHours"
                        control={control}
                        rules={{ required: true, validate: validateOvertimeWorkDelta }}
                        render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                            <TextField
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={error?.message}
                                isDecimalNumber
                                keyboardType="numeric"
                                returnKeyType="done"
                                selectTextOnFocus
                                inputStyle={{
                                    textAlign: "right",
                                    width: undefined,
                                }}
                            />
                        )}
                    />
                    {nbHours && !errors.nbHours && (
                        <BodySmallText color={colors.greyAccessible}>
                            {i18n.t("overtimePaid.hours_equivalent", {
                                hours: nbHours,
                                amount: formatCurrency({
                                    amountInCents: Number(nbHours) * hourlyInvoiceAmount,
                                }),
                            })}
                        </BodySmallText>
                    )}
                </VStack>
            </ScreenCard>
        </Modal>
    );
};

const useStyles = () => {
    const { colors, gridSize, roundness } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    paddingHorizontal: gridSize / 2,
                    paddingBottom: gridSize / 5,
                    borderRadius: roundness,
                    backgroundColor: colors.onSecondaryContainer,
                },
            }),
        [colors.onSecondaryContainer, gridSize, roundness],
    );
};

export default OvertimePaidCreateModal;
